import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AuthContext } from "shared/context/AuthContext";


const TTL = 1000 * 60 * 5;
const REQUEST_TIMEOUT = 60_000;


export const useOvirtData = () => {
  const { state, setContext } =
    useContext(AuthContext);

  // console.log("[useOvirtData] render", {
  //   isAuthUser: state.isAuthUser,
  //   areIntegrationsReady:
  //     state.areIntegrationsReady,
  //   isOvirtAvailable:
  //     state.isOvirtAvailable,
  //   hasCachedData: Array.isArray(
  //     state.ovirtData?.vms
  //   ),
  // });
  const cachedData = useMemo(() => {
    if (Array.isArray(state.ovirtData?.vms)) {
      return state.ovirtData;
    }

    return null;
  }, [state.ovirtData]);

  const cachedLastFetch =
    state.ovirtDataLastFetch || 0;

  const isCacheValid =
    cachedData !== null &&
    cachedLastFetch > 0 &&
    Date.now() - cachedLastFetch < TTL;

  const [data, setData] = useState(cachedData);
  const [loading, setLoading] = useState(
    !isCacheValid
  );

  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);
  const lastValidDataRef = useRef(cachedData);

  useEffect(() => {
    if (
      cachedData &&
      Array.isArray(cachedData.vms)
    ) {
      lastValidDataRef.current = cachedData;
      setData(cachedData);
    }
  }, [cachedData]);

  const fetchData = useCallback(
    async (force = false) => {
      if (
        !state.isAuthUser ||
        state.areIntegrationsReady !== true ||
        state.isOvirtAvailable !== true ||
        state.isOvirtSessionExpired === true
      ) {
        setLoading(false);
        return false;
      }

      if (
        !force &&
        cachedData &&
        isCacheValid
      ) {
        setData(cachedData);
        setLoading(false);
        return true;
      }

      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let didTimeout = false;

      const timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, REQUEST_TIMEOUT);

      setLoading(true);
      setError(null);

      try {
        const resp = await fetch(
          `${process.env.REACT_APP_API_URL}` +
            "/ti/api/ovirt/",
          {
            credentials: "include",
            signal: controller.signal,
          }
        );

        const result = await resp
          .json()
          .catch(() => null);

        // if (resp.status === 401) {
        //   setContext({
        //     isOvirtAvailable: false,
        //   });

        //   throw new Error(
        //     result?.error ||
        //       result?.detail ||
        //       "Brak aktywnej sesji oVirt"
        //   );
        // }
        if (resp.status === 401) {
          setContext({
            ovirtSSOToken: "",
            ovirtDataLastFetch: 0,
            isOvirtSessionExpired: true,
          });

          throw new Error(
            result?.error ||
              result?.detail ||
              "Sesja oVirt wygasła"
          );
        }

        if (!resp.ok) {
          throw new Error(
            result?.error ||
              result?.detail ||
              `HTTP ${resp.status}`
          );
        }

        if (
          !result?.ovirt_data ||
          !Array.isArray(
            result.ovirt_data.vms
          )
        ) {
          throw new Error(
            "Invalid oVirt response"
          );
        }
        const previousData =
          lastValidDataRef.current ||
          cachedData;

        const hadVirtualMachines =
          Array.isArray(previousData?.vms) &&
          previousData.vms.length > 0;

        const receivedEmptyResult =
          result.ovirt_data.vms.length === 0;

        if (
          hadVirtualMachines &&
          receivedEmptyResult
        ) {
          throw new Error(
            "oVirt zwrócił pustą listę maszyn — zachowano poprzednie dane"
          );
        }
        const fetchedAt = Date.now();

        lastValidDataRef.current =
          result.ovirt_data;

        setData(result.ovirt_data);

        setContext({
          ovirtData: result.ovirt_data,
          ovirtSSOToken:
            result.ovirt_sso_token || "",
          ovirtWebadminUrl:
            result.ovirt_webadmin_url || "",
          ovirtDataLastFetch: fetchedAt,
          isOvirtAvailable: true,
          isOvirtSessionExpired: false,
        });

        return true;
      } catch (err) {
        if (err.name === "AbortError") {
          if (didTimeout) {
            const timeoutError = new Error(
              "Przekroczono czas pobierania danych oVirt"
            );

            console.error(
              "Failed to fetch oVirt data:",
              timeoutError
            );

            setError(timeoutError);
          }

          return false;
        }

        console.error(
          "Failed to fetch oVirt data:",
          err
        );

        const fallbackData =
          lastValidDataRef.current ||
          cachedData;

        if (fallbackData) {
          setData(fallbackData);
        }

        setError(err);

        return false;
      } finally {
        clearTimeout(timeoutId);

        if (
          abortControllerRef.current === controller
        ) {
          setLoading(false);
        }
      }
    },
    [
      state.isAuthUser,
      state.areIntegrationsReady,
      state.isOvirtAvailable,
      state.isOvirtSessionExpired,
      cachedData,
      isCacheValid,
      setContext,
    ]
  );
  useEffect(() => {
    if (
      !state.isAuthUser ||
      state.areIntegrationsReady !== true ||
      state.isOvirtAvailable !== true ||
      state.isOvirtSessionExpired === true
    ) {
      setLoading(false);
      return;
    }

    fetchData();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [
    state.isAuthUser,
    state.areIntegrationsReady,
    state.isOvirtAvailable,
    state.isOvirtSessionExpired,
    fetchData,
  ]);

  return {
    data,
    loading,
    error,
    refetch: () => {
      if (
        !state.isAuthUser ||
        state.areIntegrationsReady !== true ||
        state.isOvirtAvailable !== true ||
        state.isOvirtSessionExpired === true
      ) {
        return Promise.resolve(false);
      }

      return fetchData(true);
    },
  };
};